// ============================================================================
// TENANT SERVICE
// ============================================================================
// Business logic and API calls for Tenant Management module

import { get, post, put, del } from '../client/axiosClient';
import { tenantEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ID,
  TenantResponse,
  TenantSummaryResponse,
  CreateTenantRequest,
  UpdateTenantRequest,
  SuspendTenantRequest,
  TenantHubPagedResult,
  TenantHubPagedRequest,
  TenantStatistics,
} from '../types';
import type { TenantHubApiResponse } from '../types/auth.types';

/**
 * Tenant Service Class
 */
class TenantService {
  /**
   * Get all tenants (paginated)
   */
  async getAll(params?: TenantHubPagedRequest): Promise<TenantHubApiResponse<TenantHubPagedResult<TenantResponse>>> {
    const endpoint = params
      ? buildEndpointWithQuery(tenantEndpoints.list(), params)
      : tenantEndpoints.list();

    const response = await get<TenantHubApiResponse<TenantHubPagedResult<TenantResponse>>>(endpoint);
    return response.data;
  }

  /**
   * Get tenant by ID
   */
  async getById(id: ID): Promise<TenantHubApiResponse<TenantResponse>> {
    const response = await get<TenantHubApiResponse<TenantResponse>>(tenantEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get tenant by code
   */
  async getByCode(code: string): Promise<TenantHubApiResponse<TenantResponse>> {
    const response = await get<TenantHubApiResponse<TenantResponse>>(tenantEndpoints.getByCode(code));
    return response.data;
  }

  /**
   * Get tenant summaries
   */
  async getSummaries(): Promise<TenantHubApiResponse<TenantSummaryResponse[]>> {
    const response = await get<TenantHubApiResponse<TenantSummaryResponse[]>>(tenantEndpoints.summaries());
    return response.data;
  }

  /**
   * Create new tenant
   */
  async create(data: CreateTenantRequest): Promise<TenantHubApiResponse<TenantResponse>> {
    const response = await post<TenantHubApiResponse<TenantResponse>>(
      tenantEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update tenant
   */
  async update(id: ID, data: UpdateTenantRequest): Promise<TenantHubApiResponse<TenantResponse>> {
    const response = await put<TenantHubApiResponse<TenantResponse>>(
      tenantEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete tenant
   */
  async delete(id: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await del<TenantHubApiResponse<boolean>>(tenantEndpoints.delete(id));
    return response.data;
  }

  /**
   * Suspend tenant
   */
  async suspend(id: ID, data: SuspendTenantRequest): Promise<TenantHubApiResponse<boolean>> {
    const response = await post<TenantHubApiResponse<boolean>>(
      tenantEndpoints.suspend(id),
      data
    );
    return response.data;
  }

  /**
   * Activate tenant
   */
  async activate(id: ID): Promise<TenantHubApiResponse<boolean>> {
    const response = await post<TenantHubApiResponse<boolean>>(
      tenantEndpoints.activate(id),
      {}
    );
    return response.data;
  }

  /**
   * Get tenant statistics
   */
  async getStatistics(): Promise<TenantHubApiResponse<TenantStatistics>> {
    const response = await get<TenantHubApiResponse<TenantStatistics>>(tenantEndpoints.statistics());
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const tenantService = new TenantService();
export default tenantService;
